import type {
  UpdateReadDateRequest,
  UpdateRecommendationUsage,
  WorkUserThumbRatingResult,
  WorkUserThumbRating,
  GetPaginatedWorksByEngagementStatusResponse,
  AlreadyReadEntry,
  UserWorkEngagementStatusResponse,
  UserWorkEngagementStatus,
} from "@/types/user-work-engagement.types";
import {
  WorksListSortingMethod,
  WorksListSortingOrder,
} from "@/types/user-work-engagement.types";
import { httpClient } from "@/infrastructure/http/http.service";
import { logError } from "@/infrastructure/observability/error-logging.service";
import type { SsrClient } from "@/infrastructure/http/ssr-client.service";
import type { HttpClient } from "@/infrastructure/http/http-client.factory";

const baseUrl = "tb-api/user-work-engagement";

export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 25;

/**
 * Shared fetch logic for paginated works endpoints.
 * @param url The endpoint URL to call
 * @param page The page number starting at 1
 * @param pageSize The number of works per page
 * @param sortingMethod The method by which to sort the list
 * @param sortingOrder The order aka ascending or descending
 * @param requestClient Browser HttpClient or policy-aware SsrClient.
 * @returns Returns a paginated list of works or null on error
 */
async function getPaginatedWorks(
  url: string,
  page: number,
  pageSize: number,
  sortingMethod: WorksListSortingMethod,
  sortingOrder: WorksListSortingOrder,
  requestClient: Pick<SsrClient | HttpClient, "get"> = httpClient,
): Promise<GetPaginatedWorksByEngagementStatusResponse | null> {
  try {
    const response =
      await requestClient.get<GetPaginatedWorksByEngagementStatusResponse>(
        url,
        {
          params: { page, pageSize, sortingMethod, sortingOrder },
        },
      );

    return response.data;
  } catch (e) {
    logError(e, { context: "getPaginatedWorks" });
    return null;
  }
}

/**
 * Gets the works on the user's want-to-read list
 * @param page The page number starting at 1
 * @param pageSize The number of works per page
 * @param sortingMethod The method by which to sort the list
 * @param sortingOrder The order aka ascending or descending
 * @param requestClient Browser HttpClient or policy-aware SsrClient.
 * @returns Returns a paginated list of want-to-read works or null on error
 */
export async function getPaginatedWantToReadWorks(
  page: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = DEFAULT_PAGE_SIZE,
  sortingMethod: WorksListSortingMethod = WorksListSortingMethod.DateAdded,
  sortingOrder: WorksListSortingOrder = WorksListSortingOrder.Descending,
  requestClient: Pick<SsrClient | HttpClient, "get"> = httpClient,
): Promise<GetPaginatedWorksByEngagementStatusResponse | null> {
  return getPaginatedWorks(
    `${baseUrl}/works/want-to-read`,
    page,
    pageSize,
    sortingMethod,
    sortingOrder,
    requestClient,
  );
}

/**
 * Gets the works on the user's already-read list
 * @param page The page number starting at 1
 * @param pageSize The number of works per page
 * @param sortingMethod The method by which to sort the list
 * @param sortingOrder The order aka ascending or descending
 * @param requestClient Browser HttpClient or policy-aware SsrClient.
 * @returns Returns a paginated list of already-read works or null on error
 */
export async function getPaginatedAlreadyReadWorks(
  page: number = DEFAULT_PAGE_NUMBER,
  pageSize: number = DEFAULT_PAGE_SIZE,
  sortingMethod: WorksListSortingMethod = WorksListSortingMethod.DateAdded,
  sortingOrder: WorksListSortingOrder = WorksListSortingOrder.Descending,
  requestClient: Pick<SsrClient | HttpClient, "get"> = httpClient,
): Promise<GetPaginatedWorksByEngagementStatusResponse | null> {
  return getPaginatedWorks(
    `${baseUrl}/works/already-read`,
    page,
    pageSize,
    sortingMethod,
    sortingOrder,
    requestClient,
  );
}

/**
 * Updates the user's read date for a work
 * @param idWork The work to update the row for
 * @param year Optional year, e.g. 2024
 * @param month Optional month on [1, 12], e.g. 11 for November
 * @returns Returns true if the date was updated successfully
 */
export async function updateReadDate(
  idWork: number,
  year: number | null,
  month: number | null,
): Promise<boolean> {
  try {
    const request: UpdateReadDateRequest = {
      idWork,
      year,
      month,
    };
    const response = await httpClient.put<boolean>(
      `${baseUrl}/read-date/`,
      request,
    );

    return response.data;
  } catch (e) {
    logError(e, { context: "updateReadDate" });
    return false;
  }
}

/**
 * Updates the user's recommendation usage for a specific work
 * @param idWork The work to update the recommendation usage for
 * @param useInRecommendations True if the user wishes to use this idWork in their recs
 * @returns Returns true on success
 */
export async function updateRecommendationUsage(
  idWork: number,
  useInRecommendations: boolean,
): Promise<boolean> {
  try {
    const request: UpdateRecommendationUsage = {
      idWork,
      useInRecommendations,
    };
    const response = await httpClient.put<boolean>(
      `${baseUrl}/recommendation-usage/`,
      request,
    );
    return response.data;
  } catch (e) {
    logError(e, { context: "updateRecommendationUsage" });
    return false;
  }
}

/**
 * Updates the user's thumb rating for a work.
 * @param idWork The work ID.
 * @param value The thumb rating value to set.
 * @returns A promise resolving to the API response with the updated rating.
 */
export async function updateWorkUserThumbRating(
  idWork: number,
  value: WorkUserThumbRating,
): Promise<WorkUserThumbRatingResult> {
  const response = await httpClient.put<WorkUserThumbRatingResult>(
    `${baseUrl}/work/${idWork}/thumbs-rating/`,
    { workUserThumbRating: value },
  );

  return response.data;
}

/**
 * Deletes the user's thumb rating for a work.
 * @param idWork The work ID.
 * @returns A promise resolving to the API response after deletion.
 */
export async function deleteWorkUserThumbRating(
  idWork: number,
): Promise<WorkUserThumbRatingResult> {
  const response = await httpClient.delete<WorkUserThumbRatingResult>(
    `${baseUrl}/work/${idWork}/thumbs-rating/`,
  );

  return response.data;
}

/**
 * Moves a work from want to read to already read list
 * @param idWork The work to move
 * @returns Returns the moved work if successful, or null if not
 */
export async function moveToAlreadyReadList(
  idWork: number,
): Promise<AlreadyReadEntry | null> {
  try {
    const result = await httpClient.post<AlreadyReadEntry>(
      `${baseUrl}/work/${idWork}/move-to-already-read`,
    );

    return result.data;
  } catch {
    return null;
  }
}

/**
 * Removes the engagementStatus of a work for the user.
 * @param idWork The ID of the work to remove the engagementStatus for.
 * @returns Returns true if the engagementStatus was removed successfully, false otherwise.
 */
export async function removeWorkEngagementStatus(
  idWork: number,
): Promise<boolean> {
  try {
    const response = await httpClient.delete<UserWorkEngagementStatusResponse>(
      `${baseUrl}/work/${idWork}/engagement-status/`,
    );

    return response.data.isError === false;
  } catch {
    return false;
  }
}

/**
 * Sets the engagementStatus of a work for the user.
 * @param idWork The ID of the work.
 * @param engagementStatus The engagementStatus to set.
 * @returns Returns true if the engagementStatus was set successfully, false otherwise.
 */
export async function createOrUpdateWorkEngagementStatus(
  idWork: number,
  engagementStatus: UserWorkEngagementStatus,
): Promise<boolean> {
  try {
    const response = await httpClient.put<UserWorkEngagementStatusResponse>(
      `${baseUrl}/work/${idWork}/engagement-status/`,
      { engagementStatus },
    );

    return response.data.isError === false;
  } catch {
    return false;
  }
}
